import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { useCartStore } from '../context/CartStore';
import AuthModal from '../components/AuthModal';
import { motion } from 'framer-motion';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const api = useApi();
  const { items, getTotalPrice, clearCart } = useCartStore();
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    if (!token) setShowAuthModal(true);
  }, [token]);

  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    paymentMethod: 'Credit Card',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setFormErrors({});

    const errors = {};
    if (formData.phone.replace(/\D/g, '').length < 10) errors.phone = '10 digits required';
    if (formData.street.trim().length < 2) errors.street = 'Required';
    if (formData.city.trim().length < 2) errors.city = 'Required';
    if (formData.state.trim().length < 2) errors.state = 'Required';
    if (formData.country.trim().length < 2) errors.country = 'Required';

    const zipRegex = /^\d{5,6}$/;
    if (!zipRegex.test(formData.zipCode)) errors.zipCode = 'Valid ZIP required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setLoading(false);
      return;
    }

    try {
      const orderData = {
        items,
        totalAmount: getTotalPrice(),
        shippingAddress: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country,
          phone: formData.phone,
        },
        paymentMethod: formData.paymentMethod,
      };

      const response = await api.post('/orders', orderData);
      clearCart();
      navigate(`/order-confirmation/${response.data.order._id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Error processing transaction');
    } finally {
      setLoading(false);
    }
  };

  const total = getTotalPrice() + getTotalPrice() * 0.1 + 100;

  if (!token) {
    return (
      <>
        <div className="min-h-screen bg-deepDark flex items-center justify-center -mt-20"></div>
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} actionType="order" />
      </>
    );
  }

  const inputClass = (err) => `
    w-full bg-transparent border-b ${err ? 'border-red-500' : 'border-white/20 hover:border-white/50 focus:border-gold'} 
    text-white px-0 py-3 text-sm font-light transition-colors duration-300 focus:outline-none placeholder:text-white/20 tracking-wide
  `;

  const labelClass = "block text-[10px] tracking-widest uppercase text-textMuted mb-1";

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen bg-deepDark py-12 px-4"
    >
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-serif text-white mb-12 tracking-wide border-b border-white/10 pb-6">Secure Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-20">
          {/* Checkout Form */}
          <form className="lg:col-span-2 space-y-12" onSubmit={handleSubmit}>
            {/* Shipping Information */}
            <div className="bg-surfaceDark border border-white/10 p-8 md:p-10">
              <h2 className="text-xl font-serif text-white mb-8 tracking-wide flex items-center gap-4">
                <span className="w-6 h-[1px] bg-gold"></span> Shipping Details
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-6">
                <div>
                  <label className={labelClass}>First Name</label>
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className={inputClass()} required />
                </div>
                <div>
                  <label className={labelClass}>Last Name</label>
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className={inputClass()} required />
                </div>
              </div>

              <div className="mb-6">
                <label className={labelClass}>Email Address</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass()} required />
              </div>

              <div className="mb-6">
                <label className={labelClass}>Phone Number</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass(formErrors.phone)} required />
                {formErrors.phone && <p className="text-red-500/80 text-[10px] mt-2 uppercase tracking-widest">{formErrors.phone}</p>}
              </div>

              <div className="mb-6">
                <label className={labelClass}>Street Address</label>
                <input type="text" name="street" value={formData.street} onChange={handleChange} className={inputClass(formErrors.street)} required />
                {formErrors.street && <p className="text-red-500/80 text-[10px] mt-2 uppercase tracking-widest">{formErrors.street}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mb-6">
                <div>
                  <label className={labelClass}>City</label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} className={inputClass(formErrors.city)} required />
                </div>
                <div>
                  <label className={labelClass}>State/Province</label>
                  <input type="text" name="state" value={formData.state} onChange={handleChange} className={inputClass(formErrors.state)} required />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                <div>
                  <label className={labelClass}>ZIP / Postal Code</label>
                  <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} className={inputClass(formErrors.zipCode)} required />
                </div>
                <div>
                  <label className={labelClass}>Country</label>
                  <input type="text" name="country" value={formData.country} onChange={handleChange} className={inputClass(formErrors.country)} required />
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-surfaceDark border border-white/10 p-8 md:p-10">
              <h2 className="text-xl font-serif text-white mb-8 tracking-wide flex items-center gap-4">
                <span className="w-6 h-[1px] bg-gold"></span> Payment Method
              </h2>
              <div>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  className="w-full bg-deepDark border border-white/20 text-white px-4 py-4 text-sm font-light focus:outline-none focus:border-gold transition-colors duration-300 appearance-none rounded-none"
                >
                  <option>Credit Card</option>
                  <option>Debit Card</option>
                  <option>Wire Transfer</option>
                  <option>Digital Wallet</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="bg-red-900/20 border border-red-500/50 text-white text-xs tracking-widest uppercase p-4 text-center">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-gold text-deepDark font-bold py-5 text-sm tracking-widest uppercase hover:bg-white transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Processing Transaction...' : 'Confirm Acquisition'}
            </button>
          </form>

          {/* Order Summary Sidebar */}
          <div className="h-fit lg:sticky lg:top-32">
            <h2 className="text-xl font-serif text-white mb-8 tracking-wide">Acquisition Summary</h2>
            
            <div className="bg-surfaceDark border border-white/10 p-8">
              <div className="space-y-4 mb-8 max-h-[40vh] overflow-y-auto pr-4 scrollbar-hide">
                {items.map((item) => (
                  <div key={item._id} className="flex justify-between items-start text-sm text-textMuted font-light gap-4">
                    <span className="truncate leading-relaxed">{item.quantity} × {item.name}</span>
                    <span className="flex-shrink-0 text-white font-serif">₹{(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-8 space-y-4 mb-8">
                <div className="flex justify-between text-xs tracking-widest uppercase text-textMuted">
                  <span>Subtotal</span>
                  <span className="text-white">₹{getTotalPrice().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs tracking-widest uppercase text-textMuted">
                  <span>Taxes (10%)</span>
                  <span className="text-white">₹{(getTotalPrice() * 0.1).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-xs tracking-widest uppercase text-textMuted">
                  <span>Insured Delivery</span>
                  <span className="text-white">₹100</span>
                </div>
                <div className="flex justify-between items-baseline pt-4 border-t border-white/5">
                  <span className="text-xs tracking-widest uppercase font-bold text-white">Total</span>
                  <span className="text-2xl font-serif text-gold">₹{total.toLocaleString()}</span>
                </div>
              </div>

              <div className="bg-deepDark border border-white/5 p-6">
                <ul className="text-[10px] tracking-widest uppercase text-textMuted space-y-3">
                  <li className="flex gap-3"><span className="text-gold">✦</span> Secured Data Encryption</li>
                  <li className="flex gap-3"><span className="text-gold">✦</span> Fully Insured Logistics</li>
                  <li className="flex gap-3"><span className="text-gold">✦</span> Authenticity Certificate</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CheckoutPage;
